import { User, UserProfileFormData } from '../models/user';
import { UserProfile } from '../types/user';
import { logEvent } from './analytics-service';
import { auth, firestore } from '../firebase/firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile as updateFirebaseProfile, createUserWithEmailAndPassword } from 'firebase/auth';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { OrganizationRole } from '../models/organization';
import { log } from './production-logger';

// Import organizationMembershipService dynamically to avoid circular dependency
import { httpsCallable } from 'firebase/functions';

/**
 * Service for managing user profiles
 */
class UserService {
  /**
   * Get user profile by ID
   * @param userId - User ID
   * @returns Promise with user data
   */
  async getUserById(userId: string): Promise<User | null> {
    try {
      // Direct usage of firestore instance
      const userDoc = await getDoc(doc(firestore, 'users', userId));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          id: userDoc.id,
          ...userData,
        } as User;
      }

      return null;
    } catch (error) {
      log.error('Error getting user:', error);
      throw new Error('Failed to get user profile');
    }
  }

  /**
   * Get user profile in UserProfile format (required by auth context)
   * @param userId - User ID
   * @returns Promise with UserProfile data
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      log.debug('Getting user profile for userId:', userId);
      
      // Direct usage of firestore instance
      const userDoc = await getDoc(doc(firestore, 'users', userId));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        log.debug('User document found:', { userId, hasData: !!userData });
        
        // Transform User data to UserProfile format
        const userProfile: UserProfile = {
          userId: userId,
          email: userData.email || null,
          displayName: userData.displayName || null,
          photoURL: userData.photoURL || null,
          phoneNumber: userData.phoneNumber || null,
          organizationId: userData.organizationId || null,
          role: userData.role || 'user',
          createdAt: userData.createdAt ? new Date(userData.createdAt).getTime() : Date.now(),
          updatedAt: userData.updatedAt ? new Date(userData.updatedAt).getTime() : Date.now(),
          preferences: userData.preferences || {
            theme: 'light',
            language: 'en',
            notifications: {
              email: true,
              push: true,
              inApp: true,
              invoiceCreated: true,
              paymentReceived: true,
              paymentOverdue: true,
              subscriptionRenewal: true,
              memberActivity: true,
            },
            privacy: {
              allowMemberDataSharing: false,
              allowAnonymousStatistics: true,
              profileVisibility: 'organization',
            },
            account: {
              twoFactorEnabled: false,
              emailVerificationRequired: true,
            },
          },
        };

        log.debug('User profile created successfully:', { userId, profileData: !!userProfile });
        return userProfile;
      } else {
        log.warn('User document not found for userId:', userId);
        return null;
      }
    } catch (error) {
      log.error('Error getting user profile:', error);
      throw new Error(`Failed to get user profile: ${error.message}`);
    }
  }

  /**
   * Update user profile
   * @param userId - User ID
   * @param profileData - User profile form data
   * @returns Promise with updated user data
   */
  async updateUserProfile(userId: string, profileData: UserProfileFormData): Promise<User> {
    try {
      logEvent('update_user_profile', { userId });

      // Get the current user data
      const userDoc = await getDoc(doc(firestore, 'users', userId));

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      // Update the profile in Firestore
      const updateData = {
        displayName: profileData.displayName,
        updatedAt: new Date().toISOString(),
      };

      if (profileData.photoURL) {
        Object.assign(updateData, { photoURL: profileData.photoURL });
      }

      if (profileData.preferences) {
        Object.assign(updateData, { preferences: profileData.preferences });
      }

      await updateDoc(doc(firestore, 'users', userId), updateData);

      // Update profile in Firebase Auth if the current user
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid === userId) {
        await updateFirebaseProfile(currentUser, {
          displayName: profileData.displayName,
          photoURL: profileData.photoURL,
        });
      }

      // Get the updated user data
      const updatedUserDoc = await getDoc(doc(firestore, 'users', userId));

      return {
        id: updatedUserDoc.id,
        ...updatedUserDoc.data(),
      } as User;
    } catch (error) {
      log.error('Error updating user profile:', error);
      throw new Error('Failed to update user profile');
    }
  }

  /**
   * Update specific user profile fields (partial update)
   * @param userId - User ID
   * @param updates - Partial user profile updates
   * @returns Promise with updated user data
   */
  async updateUserProfileFields(
    userId: string,
    updates: Partial<UserProfileFormData> | any,
  ): Promise<User> {
    try {
      log.debug('UserService: updateUserProfileFields called with:', { userId, updates });
      logEvent('update_user_profile_fields', { userId, fields: Object.keys(updates) });

      // Get the current user data
      const userDoc = await getDoc(doc(firestore, 'users', userId));

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      log.debug('UserService: Current user document:', userDoc.data());

      // Build update data only with provided fields
      const updateData: any = {
        updatedAt: new Date().toISOString(),
      };

      // Handle direct fields
      if (updates.displayName !== undefined) {
        updateData.displayName = updates.displayName;
      }

      if (updates.photoURL !== undefined) {
        updateData.photoURL = updates.photoURL;
      }
      
      if (updates.firstName !== undefined) {
        updateData.firstName = updates.firstName;
      }
      
      if (updates.lastName !== undefined) {
        updateData.lastName = updates.lastName;
      }
      
      if (updates.phoneNumber !== undefined) {
        updateData.phoneNumber = updates.phoneNumber;
      }
      
      // Handle profile object if provided
      if (updates.profile !== undefined && typeof updates.profile === 'object') {
        // Filter out undefined values from profile
        const cleanProfile: any = {};
        Object.entries(updates.profile).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            cleanProfile[key] = value;
          }
        });
        
        if (Object.keys(cleanProfile).length > 0) {
          updateData.profile = cleanProfile;
        }
      }

      if (updates.preferences !== undefined) {
        // Merge preferences with existing data to avoid overwriting
        const currentPreferences = userDoc.data()?.preferences || {};
        updateData.preferences = {
          ...currentPreferences,
          ...updates.preferences,
          // Deep merge notifications if provided
          ...(updates.preferences.notifications && {
            notifications: {
              ...currentPreferences.notifications,
              ...updates.preferences.notifications,
            },
          }),
          // Deep merge privacy if provided
          ...(updates.preferences.privacy && {
            privacy: {
              ...currentPreferences.privacy,
              ...updates.preferences.privacy,
            },
          }),
          // Deep merge account if provided
          ...(updates.preferences.account && {
            account: {
              ...currentPreferences.account,
              ...updates.preferences.account,
            },
          }),
        };
      }

      log.debug('UserService: Updating Firestore with data:', updateData);
      await updateDoc(doc(firestore, 'users', userId), updateData);
      log.debug('UserService: Firestore update completed');

      // Update profile in Firebase Auth if the current user (only update provided fields)
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid === userId) {
        const authUpdateData: any = {};
        if (updates.displayName !== undefined) {
          authUpdateData.displayName = updates.displayName;
        }
        if (updates.photoURL !== undefined) {
          authUpdateData.photoURL = updates.photoURL;
        }

        if (Object.keys(authUpdateData).length > 0) {
          log.debug('UserService: Updating Firebase Auth with data:', authUpdateData);
          await updateFirebaseProfile(currentUser, authUpdateData);
          log.debug('UserService: Firebase Auth update completed');
        }
      }

      // Get the updated user data
      const updatedUserDoc = await getDoc(doc(firestore, 'users', userId));
      log.debug('UserService: Retrieved updated user document:', updatedUserDoc.data());

      return {
        id: updatedUserDoc.id,
        ...updatedUserDoc.data(),
      } as User;
    } catch (error) {
      log.error('Error updating user profile fields:', error);
      throw new Error('Failed to update user profile fields');
    }
  }

  /**
   * Upload user profile photo
   * @param userId - User ID
   * @param photoFile - Photo file to upload
   * @returns Promise with download URL
   */
  async uploadProfilePhoto(userId: string, photoFile: File): Promise<string> {
    try {
      logEvent('upload_profile_photo', { userId });

      // Create a reference to the storage location
      const photoRef = ref(storage, `profile_photos/${userId}/${Date.now()}`);

      // Upload the file
      const snapshot = await uploadBytes(photoRef, photoFile);

      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      log.error('Error uploading profile photo:', error);
      throw new Error('Failed to upload profile photo');
    }
  }

  /**
   * Get users by organization ID
   * @param organizationId - Organization ID
   * @returns Promise with array of users
   */
  async getUsersByOrganization(organizationId: string): Promise<User[]> {
    try {
      const usersCollection = collection(firestore, 'users');
      const q = query(usersCollection, where('organizationId', '==', organizationId));

      const querySnapshot = await getDocs(q);
      const users: User[] = [];

      querySnapshot.forEach(doc => {
        users.push({
          id: doc.id,
          ...doc.data(),
        } as User);
      });

      return users;
    } catch (error) {
      log.error('Error getting users by organization:', error);
      throw new Error('Failed to get users by organization');
    }
  }

  /**
   * Validate user profile data
   * @param data - User profile data to validate
   * @returns Array of validation errors
   */
  validateUserProfile(data: UserProfileFormData): string[] {
    const errors: string[] = [];

    if (!data.displayName || data.displayName.trim() === '') {
      errors.push('Display name is required');
    } else if (data.displayName.length < 3) {
      errors.push('Display name must be at least 3 characters');
    }

    if (data.preferences) {
      if (data.preferences.language && data.preferences.language.length !== 2) {
        errors.push('Language code must be 2 characters');
      }

      if (data.preferences.defaultCurrency && data.preferences.defaultCurrency.length !== 3) {
        errors.push('Currency code must be 3 characters');
      }
    }

    return errors;
  }

  /**
   * Create a new user by admin with comprehensive profile data
   * @param userData - Complete user profile data
   * @returns Promise with created user data
   */
  async createUserByAdmin(userData: any): Promise<User> {
    try {
      logEvent('admin_create_user', { email: userData.email });
      
      // Check if user already exists
      const userExists = await this.checkUserExists(userData.email);
      if (userExists) {
        throw new Error('A user with this email already exists');
      }

      // Get current user and organization
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to create team members');
      }

      // Try to use Cloud Function if available
      try {
        const createUser = httpsCallable(functions, 'createUserByAdmin');
        
        // Call function with required parameters
        const result = await createUser({
          email: userData.email,
          displayName: userData.displayName || `${userData.firstName} ${userData.lastName}`,
          organizationId: userData.organizationId,
          role: userData.role || 'member',
          createdBy: currentUser.uid,
          password: userData.password // Pass the password
        });
        
        const newUser = (result.data as any).user;
        
        // Create the full user profile in Firestore with additional data
        // Filter out undefined values to prevent Firestore errors
        const profileData: any = {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          phoneNumber: userData.phoneNumber || '',
          profile: {}
        };
        
        // Only add profile fields if they have values
        const profileFields = {
          jobTitle: userData.jobTitle,
          department: userData.department,
          employeeId: userData.employeeId,
          startDate: userData.startDate,
          workLocation: userData.workLocation,
          address: userData.address,
          city: userData.city,
          state: userData.state,
          zipCode: userData.zipCode,
          country: userData.country,
          emergencyContactName: userData.emergencyContactName,
          emergencyContactPhone: userData.emergencyContactPhone,
          notes: userData.notes,
          dateOfBirth: userData.dateOfBirth,
          managerId: userData.managerId,
        };
        
        // Filter out undefined/null values
        Object.entries(profileFields).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            profileData.profile[key] = value;
          }
        });
        
        await this.updateUserProfileFields(newUser.uid, profileData);
        
        // Password is handled in the UI modal, no automatic email sending
        log.debug('User created successfully with Cloud Function');
        
        return newUser;
      } catch (functionError) {
        log.debug('Cloud Function error:', functionError);
        log.debug('Creating user with client-side approach');
        
        // Fallback: Create user with a temporary password approach
        return await this.createUserWithTempPassword(userData);
      }
    } catch (error) {
      log.error('Error creating user by admin:', error);
      throw error;
    }
  }

  /**
   * Create a pending user profile (invitation-based system)
   * @param userData - User profile data
   * @returns Promise with created user profile
   */
  async createPendingUser(userData: any): Promise<User> {
    try {
      
      // Generate a unique user ID
      const userId = `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Build profile object with only defined values
      const profile: any = {};
      const profileFields = {
        jobTitle: userData.jobTitle,
        department: userData.department,
        employeeId: userData.employeeId,
        startDate: userData.startDate,
        workLocation: userData.workLocation,
        address: userData.address,
        city: userData.city,
        state: userData.state,
        zipCode: userData.zipCode,
        country: userData.country,
        emergencyContactName: userData.emergencyContactName,
        emergencyContactPhone: userData.emergencyContactPhone,
        notes: userData.notes,
        dateOfBirth: userData.dateOfBirth,
        managerId: userData.managerId,
      };
      
      // Only include fields that have values
      Object.entries(profileFields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          profile[key] = value;
        }
      });
      
      const userProfile: User = {
        uid: userId,
        id: userId,
        email: userData.email || '',
        displayName: userData.displayName || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phoneNumber: userData.phoneNumber || '',
        profilePhotoUrl: userData.profilePhotoUrl || '',
        isEmailVerified: false,
        
        // Professional information - only include if there are fields
        ...(Object.keys(profile).length > 0 && { profile }),
        
        // System fields
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'pending', // User needs to complete registration
        
        // Preferences with defaults
        preferences: {
          language: 'en',
          defaultCurrency: 'USD',
          notifications: {
            email: true,
            push: true,
            sms: false,
          },
          theme: 'light',
        },
      };

      // Save user profile to Firestore
      await setDoc(doc(firestore, 'users', userId), {
        ...userProfile,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Send invitation email
      await this.sendInvitationEmail(
        userData.email,
        userData.displayName,
        userData.organizationName || 'Your Organization'
      );

      logEvent('pending_user_created', { 
        userId,
        email: userData.email,
        jobTitle: userData.jobTitle 
      });

      return userProfile;
    } catch (error) {
      log.error('Error creating pending user:', error);
      throw new Error('Failed to create user profile');
    }
  }

  /**
   * Create user with temporary password (fallback approach)
   * @param userData - User profile data
   * @returns Promise with created user
   */
  async createUserWithTempPassword(userData: any): Promise<User> {
    try {
      
      // Use provided password or generate a secure one
      const tempPassword = userData.password || this.generateSecurePassword();
      
      // Create Firebase Auth user directly (admin SDK not available in client)
      // For now, create a pending user and send invitation
      const pendingUser = await this.createPendingUser({
        ...userData,
        tempPassword,
        status: 'invited'
      });
      
      // Password is handled in the UI modal, no automatic email sending
      log.debug('User created with fallback method');
      
      return pendingUser;
    } catch (error) {
      log.error('Error creating user with temp password:', error);
      throw error;
    }
  }

  /**
   * Generate a secure temporary password
   */
  generateSecurePassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';

    // Ensure at least one of each type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special char

    // Fill the rest randomly
    for (let i = 4; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    // Shuffle the password
    return password
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('');
  }

  /**
   * Send invitation email with temporary password
   */
  async sendInvitationEmailWithPassword(
    email: string,
    displayName: string,
    organizationName: string,
    tempPassword: string
  ): Promise<void> {
    try {
      // TODO: Implement email service integration
      // For now, log the details for testing
      log.debug('Invitation email would be sent:', {
        to: email,
        displayName,
        organizationName,
        tempPassword,
        subject: `Welcome to ${organizationName}`,
        message: `You've been invited to join ${organizationName}. Your temporary password is: ${tempPassword}`
      });
      
      logEvent('invitation_email_sent', { 
        email, 
        organizationName,
        hasPassword: !!tempPassword 
      });
    } catch (error) {
      log.error('Error sending invitation email:', error);
      // Don't throw error for email issues - user creation should still succeed
    }
  }

  /**
   * Update user profile fields
   */
  async updateUserProfileFields(userId: string, fields: any): Promise<void> {
    try {
      
      await updateDoc(doc(firestore, 'users', userId), {
        ...fields,
        updatedAt: new Date().toISOString(),
      });
      
      logEvent('user_profile_updated', { userId, fields: Object.keys(fields) });
    } catch (error) {
      log.error('Error updating user profile fields:', error);
      throw new Error('Failed to update user profile');
    }
  }

  /**
   * Send invitation email to new user
   * @param email - User's email
   * @param displayName - User's display name
   * @param organizationName - Organization name
   */
  async sendInvitationEmail(
    email: string,
    displayName: string,
    organizationName: string
  ): Promise<void> {
    try {
      // In production, this would integrate with email service
      log.debug('Invitation email would be sent to:', {
        email,
        displayName,
        organizationName,
        subject: `You've been invited to join ${organizationName}`,
        message: `Hi ${displayName},\n\nYou've been invited to join ${organizationName}. Please download the InvoiceAI app and use this email address to create your account.\n\nWelcome to the team!`
      });
      
      logEvent('invitation_email_sent', { email });
    } catch (error) {
      log.error('Error sending invitation email:', error);
      // Don't throw here as user creation should still succeed
    }
  }

  /**
   * Generate a temporary password for new users
   * @returns Temporary password
   */
  private generateTemporaryPassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  /**
   * Send welcome email to new user
   * @param email - User's email
   * @param displayName - User's display name
   * @param tempPassword - Temporary password
   * @param organizationName - Organization name
   */
  async sendWelcomeEmail(
    email: string,
    displayName: string,
    tempPassword: string,
    organizationName: string
  ): Promise<void> {
    try {
      // In production, this would send an actual email via SendGrid, AWS SES, etc.
      // For now, we'll log the details
      log.debug('Welcome email would be sent to:', {
        email,
        displayName,
        organizationName,
        message: `Welcome to ${organizationName}! Your temporary password is: ${tempPassword}. Please change it after logging in.`
      });
      
      logEvent('welcome_email_sent', { email });
    } catch (error) {
      log.error('Error sending welcome email:', error);
      throw new Error('Failed to send welcome email');
    }
  }

  /**
   * Check if user exists by email
   * @param email - Email to check
   * @returns Promise with boolean result
   */
  async checkUserExists(email: string): Promise<boolean> {
    try {
      const usersCollection = collection(firestore, 'users');
      const q = query(usersCollection, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      return !querySnapshot.empty;
    } catch (error) {
      log.error('Error checking if user exists:', error);
      throw new Error('Failed to check user existence');
    }
  }

  /**
   * Update user password (admin only)
   * @param userId - User ID
   * @param newPassword - New password
   * @returns Promise with success result
   */
  async updateUserPassword(userId: string, newPassword: string): Promise<boolean> {
    try {
      
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to update passwords');
      }
      
      log.debug('UpdateUserPassword: Current user:', auth.currentUser.uid);
      log.debug('UpdateUserPassword: Updating password for user:', userId);
      
      // Get fresh auth token
      const idToken = await auth.currentUser.getIdToken(true);
      log.debug('UpdateUserPassword: Got fresh ID token:', !!idToken);
      
      // Use HTTP endpoint instead of callable function for better reliability
      const projectId = app.options.projectId;
      const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/updateUserPasswordHTTP`;
      
      log.debug('Calling HTTP endpoint:', functionUrl);
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          newPassword,
          authToken: idToken,
        }),
      });

      log.debug('HTTP response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        log.error('HTTP error response:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      log.debug('UpdateUserPassword: HTTP result:', result);

      if (result.success) {
        log.debug('UpdateUserPassword: Success response received', result);
        
        // Force the current user to re-authenticate if it's their own password
        if (auth.currentUser && auth.currentUser.uid === userId) {
          log.debug('UpdateUserPassword: Forcing re-authentication for current user');
          try {
            // Sign out the current user to force them to login with new password
            await auth.signOut();
            log.debug('UpdateUserPassword: User signed out successfully');
          } catch (signOutError) {
            log.warn('UpdateUserPassword: Sign out failed (non-critical):', signOutError);
          }
        }
        
        logEvent('user_password_updated', { userId });
        return true;
      } else {
        throw new Error('Password update failed');
      }
    } catch (error) {
      log.error('Error updating user password:', error);
      
      // Provide more detailed error information
      if (error.message.includes('401')) {
        throw new Error('Authentication failed. Please log out and log back in.');
      } else if (error.message.includes('403')) {
        throw new Error('You do not have permission to update this user\'s password');
      } else if (error.message.includes('404')) {
        throw new Error('User not found in organization');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to update user password');
      }
    }
  }
}

const userService = new UserService();

export { userService };
export default userService;
